import { useState, useEffect, useRef, useCallback } from 'react'
import { isRecord } from '@/utils/typeGuards'
import { MonacoSqlEditor } from './MonacoSqlEditor'
import { PlayIcon } from '@heroicons/react/24/outline'
import { parseQueries, type QueryBlock } from '@/utils/sqlParser'

interface WorkbenchSqlEditorProps {
  value: string
  onChange: (value: string) => void
  onExecuteQuery: (query: string) => void
  onSelectionChange?: (selection: { startLine: number; startColumn: number; endLine: number; endColumn: number }) => void
  language?: 'mysql' | 'pgsql'
  readOnly?: boolean
  datasourceId?: string
  databaseName?: string
  schemaName?: string
  databaseType?: string
  isExecuting?: boolean
}

export function WorkbenchSqlEditor({
  value,
  onChange,
  onExecuteQuery,
  onSelectionChange,
  language,
  readOnly = false,
  datasourceId = '',
  databaseName = '',
  schemaName,
  databaseType,
  isExecuting = false
}: WorkbenchSqlEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<unknown>(null) // Reference to Monaco editor instance
  const [queryBlocks, setQueryBlocks] = useState<QueryBlock[]>([])
  const [lineHeight, setLineHeight] = useState(20) // Monaco default line height
  const [scrollTop, setScrollTop] = useState(0) // Track editor scroll position
  const [renderKey, setRenderKey] = useState(0) // Force re-render when Monaco state changes
  const listenersRef = useRef<(() => void)[]>([]) // Track event listeners for cleanup
  
  // Cleanup function to remove listeners
  const cleanupListeners = useCallback(() => {
    for (const dispose of listenersRef.current) dispose()
    listenersRef.current = []
  }, [])

  // Parse SQL queries separated by semicolon with PostgreSQL dollar-quote support

  // Editor runtime type guards/helpers
  interface EditorLike {
    getOption: (id: number) => number
    onDidScrollChange: (callback: (e: { scrollTop: number }) => void) => { dispose: () => void }
    onDidChangeModelContent: (callback: () => void) => { dispose: () => void }
    onDidChangeHiddenAreas: (callback: () => void) => { dispose: () => void }
    onDidLayoutChange: (callback: () => void) => { dispose: () => void }
    onDidChangeViewZones: (callback: () => void) => { dispose: () => void }
    getValue: () => string
    setValue: (value: string) => void
    getVisibleRanges?: () => unknown[]
    getTopForLineNumber?: (lineNumber: number) => number
  }
  const isEditorLike = (obj: unknown): obj is EditorLike => {
    if (!obj || typeof obj !== 'object') return false
    return (
      typeof (obj as { getOption?: unknown }).getOption === 'function' &&
      typeof (obj as { getValue?: unknown }).getValue === 'function'
    )
  }
  const getEditor = (): EditorLike | null => {
    return isEditorLike(editorRef.current) ? editorRef.current : null
  }

  // Handle Monaco editor mount - simplified approach
  const handleEditorMount = useCallback(() => {
    // Clean up any existing listeners first
    cleanupListeners()
    
    // Get Monaco editor instance after mount
    const checkEditor = () => {
      let monacoEditor: unknown
      const w: unknown = window
      if (isRecord(w) && 'monacoEditor' in w) {
        const { monacoEditor: me } = w as { monacoEditor?: unknown }
        monacoEditor = me
      }
      if (isEditorLike(monacoEditor)) {
        const editor = monacoEditor
        editorRef.current = editor
        
        try {
          // Get line height from editor configuration
          const lineHeightOption = editor.getOption(59) // lineHeight option ID
          if (lineHeightOption && lineHeightOption > 0) {
            setLineHeight(lineHeightOption)
            console.log('Line height from editor:', lineHeightOption)
          }
          
          // Listen to scroll events
          const scrollDisposable = editor.onDidScrollChange((e) => {
            setScrollTop(e.scrollTop)
          })
          listenersRef.current.push(() => { scrollDisposable.dispose() })
          
          // Track timeout for debouncing
          let contentChangeTimeout: NodeJS.Timeout | null = null
          
          // Listen to model content changes with debouncing
          const contentDisposable = editor.onDidChangeModelContent(() => {
            // Clear previous timeout
            if (contentChangeTimeout) {
              clearTimeout(contentChangeTimeout)
            }
            
            // Debounce updates to prevent too many re-renders
            contentChangeTimeout = setTimeout(() => {
              // Content changed - re-parse queries and trigger re-render for button positioning
              const currentContent = editor.getValue()
              const blocks = parseQueries(currentContent)
              setQueryBlocks(blocks)
              setRenderKey(prev => prev + 1)
            }, 150) // Increased delay for better stability
          })
          listenersRef.current.push(() => { contentDisposable.dispose(); if (contentChangeTimeout) { clearTimeout(contentChangeTimeout) } })
          
          // Listen to folding changes specifically
          const foldingDisposable = editor.onDidChangeHiddenAreas(() => {
            console.log('Folding state changed')
            // Folding state changed - trigger re-render for button positioning
            setRenderKey(prev => prev + 1)
          })
          listenersRef.current.push(() => { foldingDisposable.dispose() })
          
          // Listen to layout changes
          const layoutDisposable = editor.onDidLayoutChange(() => {
            // Layout changed - trigger re-render for button positioning
            setRenderKey(prev => prev + 1)
          })
          listenersRef.current.push(() => { layoutDisposable.dispose() })
          
          // Listen to view zones changes (affects layout)
          const viewZonesDisposable = editor.onDidChangeViewZones(() => {
            // View zones changed - trigger re-render for button positioning
            setRenderKey(prev => prev + 1)
          })
          listenersRef.current.push(() => { viewZonesDisposable.dispose() })
          
          // Initial setup complete - buttons will render correctly
          
        } catch (error) {
          console.log('Error setting up Monaco listeners:', error)
        }
      } else {
        // Retry if editor not ready
        setTimeout(checkEditor, 100)
      }
    }
    
    checkEditor()
  }, [cleanupListeners])
  
  
  // Setup editor when component mounts and cleanup on unmount
  useEffect(() => {
    handleEditorMount()
    
    // Cleanup listeners on unmount
    return () => {
      cleanupListeners()
    }
  }, [handleEditorMount, cleanupListeners])
  
  // Update query blocks when SQL changes - this is critical for tab switching
  useEffect(() => {
    // Get current content from editor (in case of user typing) or use prop value
    let currentContent = value
    const ed = getEditor()
    if (ed) {
      currentContent = ed.getValue()
    }
    
    const blocks = parseQueries(currentContent)
    setQueryBlocks(blocks)
    
    // Ensure Monaco editor content matches the prop (important for tab switching)
    const ed2 = getEditor()
    if (ed2 && value !== currentContent) {
      const currentEditorValue = ed2.getValue()
      if (currentEditorValue !== value) {
        ed2.setValue(value)
      }
    }
  }, [value, lineHeight, renderKey])
  
  
  // Handle execute individual query
  const handleExecuteQuery = (block: QueryBlock) => {
    onExecuteQuery(block.query)
  }
  
  
  // Calculate the visible height of a query block considering folding
  const getQueryBlockVisibleHeight = useCallback((block: QueryBlock): number => {
    const totalLines = block.endLine - block.startLine + 1
    const fullHeight = totalLines * lineHeight
    
    if (!editorRef.current) {
      return fullHeight
    }
    
    try {
      const editor = getEditor()
      
      // Use Monaco's API to check if lines are hidden
      if (editor && editor.getVisibleRanges && editor.getTopForLineNumber) {
        const visibleRanges = editor.getVisibleRanges()
        
        // First check if block start is visible
        const isStartVisible = visibleRanges.some((range: unknown) => {
          if (range && typeof range === 'object' && 'startLineNumber' in range && 'endLineNumber' in range) {
            const start = (range as { startLineNumber?: unknown }).startLineNumber
            const end = (range as { endLineNumber?: unknown }).endLineNumber
            if (typeof start === 'number' && typeof end === 'number') {
              return block.startLine >= start && block.startLine <= end
            }
          }
          return false
        })
        
        if (!isStartVisible) {
          // If start line is not visible, the whole block is hidden
          return 0
        }
        
        // Check if the block is folded by comparing line positions
        const startTop = editor.getTopForLineNumber(block.startLine)
        const endTop = editor.getTopForLineNumber(block.endLine)
        
        // If start and end have same top position, block is folded
        if (startTop === endTop) {
          return lineHeight // Folded to single line
        }
        
        // Count actual visible lines in the block
        let visibleLinesInBlock = 0
        let lastVisibleTop = -1
        
        for (let line = block.startLine; line <= block.endLine; line++) {
          const isLineVisible = visibleRanges.some((range: unknown) => {
            if (range && typeof range === 'object' && 'startLineNumber' in range && 'endLineNumber' in range) {
              const start = (range as { startLineNumber?: unknown }).startLineNumber
              const end = (range as { endLineNumber?: unknown }).endLineNumber
              if (typeof start === 'number' && typeof end === 'number') {
                return line >= start && line <= end
              }
            }
            return false
          })
          
          if (isLineVisible) {
            const lineTop = editor.getTopForLineNumber(line)
            // Only count if this line has a different top position (not folded with previous)
            if (lineTop !== lastVisibleTop) {
              visibleLinesInBlock++
              lastVisibleTop = lineTop
            }
          }
        }
        
        // Return height based on visible lines
        return Math.max(visibleLinesInBlock * lineHeight, lineHeight)
      }
      
      // Fallback to full height if API not available
      return fullHeight
    } catch (error) {
      console.log('Error calculating query height:', error)
      return fullHeight
    }
  }, [lineHeight])
  
  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      {/* Monaco Editor */}
      <MonacoSqlEditor
        value={value}
        onChange={onChange}
        onSelectionChange={onSelectionChange}
        onEditorMount={handleEditorMount}
        language={language}
        readOnly={readOnly}
        datasourceId={datasourceId}
        databaseName={databaseName}
        schemaName={schemaName}
        databaseType={databaseType}
      />
      
      {/* Query Execute Buttons Overlay */}
      {queryBlocks.length > 0 && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {queryBlocks.map((block, index) => {
            // Calculate button position using Monaco's API
            let buttonTop = 0
            
            if (getEditor()) {
              const editor = getEditor()
              
              // Use Monaco's getTopForLineNumber which handles folding automatically
              if (editor && editor.getTopForLineNumber && typeof editor.getTopForLineNumber === 'function') {
                try {
                  const lineTop = editor.getTopForLineNumber(block.startLine)
                  if (!Number.isNaN(lineTop)) {
                    buttonTop = lineTop - scrollTop
                  } else {
                    // Fallback to simple calculation
                    console.warn(`Invalid lineTop for line ${block.startLine}: ${lineTop}`)
                    buttonTop = (block.startLine - 1) * lineHeight - scrollTop
                  }
                } catch (e) {
                  // Fallback to simple calculation
                  console.log(`Failed to get top for line ${block.startLine}:`, e)
                  buttonTop = (block.startLine - 1) * lineHeight - scrollTop
                }
              } else {
                // Fallback if method not available
                buttonTop = (block.startLine - 1) * lineHeight - scrollTop
              }
            } else {
              // No editor reference yet
              buttonTop = (block.startLine - 1) * lineHeight - scrollTop
            }
            
            // Don't render if way outside visible area (be more lenient)
            if (buttonTop < -200 || buttonTop > ((containerRef.current?.clientHeight || 600) + 1000)) return null
            
            return (
            <div
              key={`button-${index}-${block.startLine}-${block.startColumn}-${block.endColumn}`}
              className="absolute pointer-events-auto group"
              data-query-index={index}
              data-start-line={block.startLine}
              data-button-top={buttonTop}
              style={{
                top: `${buttonTop}px`,
                left: '56px', // Position in the line decoration area
                zIndex: 10,
                // Force hardware acceleration for smoother updates
                transform: 'translateZ(0)',
                willChange: 'top'
              }}
            >
              {/* Query block background highlight */}
              <div 
                className="absolute bg-green-500/10 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                data-hover-block={`query-${index + 1}`}
                style={{
                  left: '-56px', // Offset to start from editor margin
                  top: '0px',
                  width: 'calc(100vw - 64px)', // Extend almost to the edge
                  height: `${getQueryBlockVisibleHeight(block)}px`,
                  minHeight: `${lineHeight}px`, // Minimum one line height
                  maxWidth: '2000px', // Max width to prevent excessive extension
                  zIndex: 5, // Ensure it's below the button but above content
                  pointerEvents: 'none' // Ensure it doesn't interfere with clicking
                }}
              />
              
              <button type="button"
                onClick={() => { handleExecuteQuery(block) }}
                disabled={isExecuting}
                className="flex items-center justify-center w-5 h-5 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed border border-white dark:border-gray-800 relative z-20"
                title={`Execute query ${index + 1}: ${block.query.substring(0, 100)}${block.query.length > 100 ? '...' : ''}`}
              >
                <PlayIcon className="w-2.5 h-2.5 ml-0.5" />
              </button>
            </div>
            )
          })}
          
          {/* Query separators */}
          {queryBlocks.length > 1 && (
            <>
              {queryBlocks.slice(0, -1).map((block, index) => {
                // Calculate separator position
                let separatorTop
                
                if (getEditor()) {
                  const editor = getEditor()
                  if (editor && editor.getTopForLineNumber) {
                    try {
                      const lineTop = editor.getTopForLineNumber(block.endLine)
                      separatorTop = lineTop + lineHeight * 0.5 - scrollTop
                    } catch {
                      separatorTop = (block.endLine + 0.5) * lineHeight - scrollTop
                    }
                  } else {
                    separatorTop = (block.endLine + 0.5) * lineHeight - scrollTop
                  }
                } else {
                  separatorTop = (block.endLine + 0.5) * lineHeight - scrollTop
                }
                
                // Don't render separator if way outside visible area
                if (separatorTop < -200 || separatorTop > ((containerRef.current?.clientHeight || 600) + 1000)) return null
                
                return (
                <div
                  key={`separator-${index}`}
                  className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent opacity-40"
                  style={{
                    top: `${separatorTop}px`
                  }}
                />
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}
